import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { PostHogInterface } from "posthog-js";
import { PostHogContext, type PostHogContextValue } from "./context";
import PostHogPageView from "./pageview";
import { scheduleIdle } from "@/lib/utils/scheduleIdle";

const POSTHOG_KEY = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;

export default function TrainrrPostHogProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [client, setClient] = useState<PostHogInterface | null>(null);
  const [flagsRevision, setFlagsRevision] = useState(0);
  const initStarted = useRef(false);

  useEffect(() => {
    if (!POSTHOG_KEY || initStarted.current) return;
    initStarted.current = true;
    const apiKey = POSTHOG_KEY;

    const start = () => {
      void import("posthog-js").then(({ default: posthog }) => {
        posthog.init(apiKey, {
          api_host: POSTHOG_HOST,
          ui_host: "https://eu.posthog.com",
          capture_pageview: false,
          capture_dead_clicks: false,
          capture_heatmaps: false,
          disable_session_recording: true,
          disable_surveys: true,
          disable_external_dependency_loading: true,
          loaded: (ph) => {
            setClient(ph);
            setFlagsRevision((revision) => revision + 1);
          },
        });
      });
    };

    return scheduleIdle(start);
  }, []);

  useEffect(() => {
    if (!client) return;
    return client.onFeatureFlags(() => {
      setFlagsRevision((revision) => revision + 1);
    });
  }, [client]);

  const value = useMemo<PostHogContextValue>(
    () => ({ client, flagsRevision }),
    [client, flagsRevision],
  );

  return (
    <PostHogContext.Provider value={value}>
      <PostHogPageView />
      {children}
    </PostHogContext.Provider>
  );
}
